import { api } from "./client";
import type { ApiResponse } from "./types";

// ═══════════════════════════════════════════════════════════════════
// The organiser's desk: a page's team, sending a page again after a no,
// updates edited or taken back, the waitlist, replies to ratings, and the
// host's people.
// ═══════════════════════════════════════════════════════════════════

export type TeamRole = "co_host" | "door";

export interface TeamPerson {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface TeamInvite {
  id: string;
  eventOrganizerId: string;
  role: string;
  status: "pending" | "accepted" | "declined" | "withdrawn";
  createdAt: string;
  respondedAt: string | null;
  invitee: TeamPerson;
  invitedBy: TeamPerson;
}

export interface Team {
  eventOrganizerId: string;
  members: Array<{ userId: string; role: string; joinedAt: string; user: TeamPerson }>;
  pendingInvites: TeamInvite[];
}

export interface MyTeamInvite {
  id: string;
  role: string;
  status: string;
  createdAt: string;
  invitedBy: TeamPerson;
  page: { id: string; displayName: string; avatarUrl: string | null; isVerified: boolean };
}

export function getTeam(pageId: string) {
  return api.get<ApiResponse<Team>>(`/event-organizers/${pageId}/team`);
}

export function inviteToTeam(pageId: string, userId: string, role: TeamRole) {
  return api.post<ApiResponse<TeamInvite>>(`/event-organizers/${pageId}/team/invites`, { userId, role });
}

export function withdrawTeamInvite(pageId: string, inviteId: string) {
  return api.delete<ApiResponse<{ inviteId: string; status: "withdrawn" }>>(
    `/event-organizers/${pageId}/team/invites/${inviteId}`,
  );
}

export function changeTeamRole(pageId: string, userId: string, role: TeamRole) {
  return api.patch<ApiResponse<Team["members"][number]>>(`/event-organizers/${pageId}/team/${userId}`, { role });
}

export function removeTeamMember(pageId: string, userId: string) {
  return api.delete<ApiResponse<{ userId: string; removed: true }>>(`/event-organizers/${pageId}/team/${userId}`);
}

export function listMyTeamInvites() {
  return api.get<ApiResponse<{ invites: MyTeamInvite[] }>>("/event-organizer-invites");
}

export function respondToTeamInvite(inviteId: string, accept: boolean) {
  return api.post<ApiResponse<{ inviteId: string; status: "accepted" | "declined"; eventOrganizerId: string }>>(
    `/event-organizer-invites/${inviteId}/respond`,
    { accept },
  );
}

// ─── Updates edited or taken back ──────────────────────────────────

export function editEventUpdate(
  eventId: string,
  updateId: string,
  change: { title?: string | null; body?: string; imageUrl?: string | null; isPinned?: boolean },
) {
  return api.patch<ApiResponse<{ id: string; body: string; isPinned: boolean }>>(
    `/events/${eventId}/updates/${updateId}`,
    change,
  );
}

export function deleteEventUpdate(eventId: string, updateId: string) {
  return api.delete<void>(`/events/${eventId}/updates/${updateId}`);
}

// ─── The host's waitlist ───────────────────────────────────────────

export interface HostWaitlistEntry {
  id: string;
  position: number | null;
  status: string;
  seats: number;
  joinedAt: string;
  offerExpiresAt: string | null;
  user: TeamPerson;
}

export function getHostWaitlist(eventId: string) {
  return api.get<
    ApiResponse<{ waitingCount: number; offeredCount: number; seatsWaiting: number; entries: HostWaitlistEntry[] }>
  >(`/events/${eventId}/waitlist`);
}

export function offerNextOnWaitlist(eventId: string) {
  return api.post<ApiResponse<HostWaitlistEntry>>(`/events/${eventId}/waitlist/offer-next`);
}

// ─── The host's reply to a rating ──────────────────────────────────

export function respondToFeedback(eventId: string, feedbackId: string, response: string) {
  return api.put<ApiResponse<{ id: string; organizerResponse: string | null; respondedAs: { name: string } | null }>>(
    `/events/${eventId}/feedback/${feedbackId}/response`,
    { response },
  );
}

// ─── The host's people: counts only ────────────────────────────────

export interface HostPeople {
  followerCount: number;
  rootsFloor: number;
  roots: Array<{ state: string; label: string; count: number }>;
  cameBack: { people12m: number; cameBack: number };
}

export function getEventOrganizerPeople(pageId: string) {
  return api.get<ApiResponse<HostPeople>>(`/event-organizers/${pageId}/people`);
}

export function getInstitutionPeople(institutionId: string) {
  return api.get<ApiResponse<HostPeople>>(`/institutions/${institutionId}/people`);
}

// ─── Send a page again after a no ──────────────────────────────────

export function resubmitMyEventOrganizer(note?: string) {
  return api.post<
    ApiResponse<{ id: string; verificationRequest: { id: string; status: string; resubmitNote: string | null } | null }>
  >("/event-organizers/me/resubmit", note ? { note } : {});
}
